import React from 'react';
import { Button, Typography, makeStyles } from '@material-ui/core';
import GitHubIcon from '@material-ui/icons/GitHub';
import { Progress, UserIdentity } from '@backstage/core-components';
import {
  SignInPageProps,
  githubAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';

/**
 * GitHub SSO sign-in page styled after the Keycloak (Red Hat SSO) login
 * used by Red Hat Developer Hub deployments: near-black backdrop, spaced
 * uppercase realm title, white card with a blue top accent bar, thin
 * "Sign in to your account" heading, and a PatternFly primary action.
 *
 * Follows the Backstage "custom SignInPage" pattern: silent session check
 * on mount, explicit popup on click, identity handed over via
 * UserIdentity.create.
 */

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10vh 24px 24px',
    // Flat near-black, same in both themes (matches the Keycloak backdrop)
    background: '#1b1b1d',
  },
  realmTitle: {
    fontFamily: "'Red Hat Display', 'Helvetica Neue', sans-serif",
    fontSize: 'clamp(22px, 3vw, 34px)',
    fontWeight: 300,
    color: '#f0f0f0',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    textAlign: 'center',
    margin: 0,
  },
  card: {
    width: '100%',
    maxWidth: 500,
    marginTop: 64,
    background: '#ffffff',
    borderTop: '4px solid #0066CC',
    borderRadius: 0,
    padding: '40px 48px 48px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  heading: {
    fontFamily: "'Red Hat Display', 'Helvetica Neue', sans-serif",
    fontSize: '1.5rem',
    fontWeight: 300,
    color: '#3c3f42',
    textAlign: 'center',
    margin: '0 0 32px',
  },
  // PatternFly primary action: flat, full width, square-ish corners
  button: {
    width: '100%',
    backgroundColor: '#0066CC',
    color: '#ffffff',
    borderRadius: 3,
    padding: '12px 16px',
    fontFamily: "'Red Hat Text', 'Helvetica Neue', sans-serif",
    fontSize: '0.9375rem',
    fontWeight: 500,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#004080',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(0, 102, 204, 0.5)',
      color: '#ffffff',
    },
  },
  hint: {
    fontFamily: "'Red Hat Text', 'Helvetica Neue', sans-serif",
    fontSize: '0.875rem',
    color: '#6a6e73',
    textAlign: 'center',
    margin: '0 0 20px',
  },
  error: {
    marginTop: 16,
    fontFamily: "'Red Hat Text', 'Helvetica Neue', sans-serif",
    fontSize: '0.8125rem',
    // PatternFly danger red
    color: '#c9190b',
    textAlign: 'center',
  },
});

export function CustomSignInPage(props: SignInPageProps) {
  const classes = useStyles();
  const authApi = useApi(githubAuthApiRef);
  const { onSignInSuccess } = props;

  // 'checking': silent session probe on mount; then show the login card
  const [checking, setChecking] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();

  const completeSignIn = React.useCallback(
    async (usePopup: boolean) => {
      const identityResponse = await authApi.getBackstageIdentity(
        usePopup ? { instantPopup: true } : { optional: true },
      );
      if (!identityResponse) {
        return false;
      }
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
        }),
      );
      return true;
    },
    [authApi, onSignInSuccess],
  );

  // Silent sign-in: restore an existing GitHub session without any popup.
  React.useEffect(() => {
    completeSignIn(false)
      .then(ok => {
        if (!ok) setChecking(false);
      })
      .catch(() => setChecking(false));
  }, [completeSignIn]);

  const handleSignIn = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const ok = await completeSignIn(true);
      if (!ok) {
        setError('Sign-in was cancelled. Please try again.');
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Sign-in failed. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className={classes.root}>
        <Progress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography component="h1" className={classes.realmTitle}>
        Appointy Developer Hub
      </Typography>
      <div className={classes.card}>
        <Typography component="h2" className={classes.heading}>
          Sign in to your account
        </Typography>
        <Typography component="p" className={classes.hint}>
          Use your GitHub account to continue.
        </Typography>
        <Button
          variant="contained"
          disableElevation
          className={classes.button}
          startIcon={<GitHubIcon />}
          onClick={handleSignIn}
          disabled={busy}
        >
          {busy ? 'Signing in…' : 'Sign in with GitHub'}
        </Button>
        {error ? <div className={classes.error}>{error}</div> : null}
      </div>
    </div>
  );
}
